import type { Client } from '@accounts/transport';
import type { Sender } from './types';

/**
 * The way back to the product that sent a person here.
 *
 * A product that is the interface for something Accounts owns — Projects, for organizations and their
 * members — sends the person here to change it, as `?product=<id>&return=<address>`. Whoever wrote
 * that link chose the address, so it is never followed as it arrived: the service is asked whether the
 * product may be returned to there, and only what it answers is kept or navigated to. A refused or
 * unanswered address leaves nothing behind, and the screen simply offers no way back.
 *
 * What is kept is the validated answer, in `sessionStorage`, so it lasts as long as the tab's visit
 * and travels between screens. It holds an address of a configured product and no credential.
 */
export class Return {
	static KEY = 'beyond-accounts-return';
	/** The query parameters a product sends a person here with */
	static PRODUCT = 'product';
	static ADDRESS = 'return';
	/**
	 * This interface's own product name. Accounts' own family bar completes its profile menu's links with
	 * it, as every product's bar does; a request to come back to Accounts itself is not a trip from
	 * another product and leaves the way back that is kept as it was.
	 */
	static OWN = 'accounts';

	#client: Client;
	#storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

	/**
	 * @param storage Injected for tests. Absent storage is a supported state, not a failure: the way
	 * back then lasts for one screen instead of one visit.
	 */
	constructor(client: Client, storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined = Return.#session()) {
		this.#client = client;
		this.#storage = storage;
	}

	static #session(): Storage | undefined {
		try {
			return globalThis.sessionStorage;
		} catch {
			return undefined;
		}
	}

	/**
	 * Whether an address carries a request to be returned somewhere
	 */
	static requested(parameters: URLSearchParams): boolean {
		return Boolean(parameters.get(Return.PRODUCT) && parameters.get(Return.ADDRESS));
	}

	/**
	 * The way back once the task the product sent the person for is done (D54): the validated address
	 * with `done=<task>` and what the task produced, so the product reads it back and shows it. Only the
	 * query changes; the origin and path the service validated stay as they were, and a value set here
	 * replaces one of the same name (the organization the person left from, for instance).
	 *
	 * @param destination A validated sender's destination
	 * @param task What was done, as `organization` for a created organization
	 * @param values What the task produced, as `{organization: <id>}`
	 */
	static done(destination: string, task: string, values: Record<string, string> = {}): string {
		const address = new URL(destination);
		address.searchParams.set('done', task);
		for (const [name, value] of Object.entries(values)) address.searchParams.set(name, value);
		return address.toString();
	}

	/**
	 * Asks the service whether the arrival may be returned, and keeps the answer.
	 *
	 * A new arrival replaces whatever was kept, whether or not it is accepted: someone who arrives from
	 * somewhere else is no longer on the earlier trip. A request naming Accounts itself (a link of its own
	 * bar opened in a new tab) asks nothing and keeps the trip.
	 *
	 * @returns The validated sender, or undefined when the service refused or could not answer. Never
	 * the value that arrived.
	 */
	async arrive(parameters: URLSearchParams): Promise<Sender | undefined> {
		const product = parameters.get(Return.PRODUCT);
		const address = parameters.get(Return.ADDRESS);
		if (!product || !address || product === Return.OWN) return this.current;

		this.forget();
		try {
			const query = new URLSearchParams({ product, destination: address });
			const sender = Return.#valid(await this.#client.get<unknown>(`/v1/return?${query.toString()}`));
			if (sender) this.#write(sender);
			return sender;
		} catch {
			return undefined;
		}
	}

	/** The validated sender of this visit, when there is one */
	get current(): Sender | undefined {
		try {
			const kept = this.#storage?.getItem(Return.KEY);
			return kept ? Return.#valid(JSON.parse(kept)) : undefined;
		} catch {
			return undefined;
		}
	}

	forget(): void {
		try {
			this.#storage?.removeItem(Return.KEY);
		} catch {
			// Storage that cannot be written keeps nothing, which is the state being asked for
		}
	}

	#write(sender: Sender): void {
		try {
			this.#storage?.setItem(Return.KEY, JSON.stringify(sender));
		} catch {
			// The way back then lasts for this screen only
		}
	}

	/**
	 * The shape of an answer, checked again on the way out of storage. Only a web address is ever
	 * handed to a link, whatever was kept.
	 */
	static #valid(value: unknown): Sender | undefined {
		const { product, name, destination } = (value ?? {}) as Partial<Sender>;
		if (typeof product !== 'string' || typeof name !== 'string' || typeof destination !== 'string') return undefined;
		try {
			if (!['http:', 'https:'].includes(new URL(destination).protocol)) return undefined;
		} catch {
			return undefined;
		}
		return { product, name, destination };
	}
}
